// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import { SectionItem } from '../Section';
import Hint from '../Hint';

import './FormField.less';

/**
 * `FormField` component.
 */
class FormField extends Component {
    constructor(props) {
        super(props);
        this.state = {
            value: this.props.value,
        };

        this.fieldApi = {
            getId: () => {
                if (this.props.id) {
                    return this.props.id;
                }
                if (this.props.name) {
                    return this.props.name.replace(/[[\]]+/g, '-').replace(/-+$/, '');
                }
                return null;
            },
            getName: () => this.props.name,
            getValue: def => {
                if (this.context.form && this.props.name) {
                    return this.context.form.getValue(this.props.name, def);
                }
                return this.state.value || def;
            },
            setValue: value => {
                this.setState({ value });
                if (this.context.form && this.props.name) {
                    this.context.form.setValue(this.props.name, value);
                }
                if (this.props.onChange) {
                    this.props.onChange(value);
                }
            },
            getErrors: () => {
                if (this.context.form && this.props.name) {
                    return this.context.form.getErrors(this.props.name);
                }
                return this.props.errors;
            },
            getRequiredMark: () => {
                if (this.context.form) {
                    return this.context.form.getRequiredMark();
                }
                return null;
            },
        };
    }

    componentWillReceiveProps({ value }) {
        if (this.isControlled()) {
            this.setState({ value });
        }
    }

    isControlled({ value } = this.props) {
        return value !== undefined;
    }

    render() {
        const {
            baseClassName,
            className,
            id,
            name,
            value,
            onChange,
            label,
            description,
            errors,
            required,
            children,
            ...props
        } = this.props;

        const fieldErrors = this.fieldApi.getErrors();

        return (
            <SectionItem
                className={classNames(
                    baseClassName,
                    {
                        [`${baseClassName}--error`]: Object.keys(this.fieldApi.getErrors()).length,
                    },
                    className,
                )}
                title={label === null ? null : <label htmlFor={this.fieldApi.getId()}>{label} {required ? this.fieldApi.getRequiredMark() : null}</label>}
                {...props}
            >
                {typeof children === 'function' ? children(this.fieldApi) : children}
                {Object.keys(fieldErrors).map(key => (
                    <span key={key} className={`${baseClassName}__error`}>{fieldErrors[key]}</span>
                ))}
                {description ? (
                    <Hint className={`${baseClassName}__description`}>{description}</Hint>
                ) : null}
            </SectionItem>
        );
    }
}
FormField.propTypes = {
    /**
     * Field ID.
     */
    id: PropTypes.string,
    /**
     * Field name.
     */
    name: PropTypes.string,
    /**
     * Field value.
     */
    value: PropTypes.any,
    /**
     * A callback function, can be executed when the value is changing.
     */
    onChange: PropTypes.func,
    /**
     * Field label.
     */
    label: PropTypes.any,
    /**
     * Field description.
     */
    description: PropTypes.any,
    /**
     * Field errors.
     */
    errors: PropTypes.object,
    /**
     * Indicates whether field is required.
     */
    required: PropTypes.bool,
    /**
     * Content of the `FormField`.
     */
    children: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.func,
    ]),
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};
FormField.defaultProps = {
    id: undefined,
    name: undefined,
    value: undefined,
    onChange: null,
    label: undefined,
    description: undefined,
    errors: {},
    required: false,
    children: undefined,
    className: undefined,
    baseClassName: `${CLS_PREFIX}form-field`,
};
FormField.contextTypes = {
    form: PropTypes.object,
};

export default FormField;



// WEBPACK FOOTER //
// ./components/FormField/FormField.js